import * as React from 'react';
import {
  Alert,
  Button,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Select,
  SelectList,
  SelectOption,
  TextArea,
  TextInput
} from '@patternfly/react-core';
import { AnnouncementPayload, NotificationApi } from '@app/api';
import DateTimePicker from '@app/components/DateTimePicker';
import { AUDIENCE_SCOPE } from '@app/model/Enums';
import { ApiError } from '@app/utils/ApiError';

/** What each scope is called in the form, and what it needs the sender to name. */
const SCOPES: { scope: AUDIENCE_SCOPE; label: string; description: string }[] = [
  { scope: AUDIENCE_SCOPE.ALL, label: 'Everyone', description: 'Every signed-in user' },
  { scope: AUDIENCE_SCOPE.ROLE, label: 'A role', description: 'Everybody holding one role' },
  { scope: AUDIENCE_SCOPE.USER, label: 'One user', description: 'A single named user' }
];

const labelOf = (scope: AUDIENCE_SCOPE) => SCOPES.find((s) => s.scope === scope)?.label ?? String(scope);

/**
 * What to tell the sender when the send fails.
 *
 * The awkward case is a rejected announcement: the service answers 400 with an <b>empty body</b>,
 * so there is no reason to relay and the generic parse of nothing at all comes out as "An unknown
 * error occurred". The fields this form validates cover every 400 it can currently produce, so
 * reaching one means either a rule that arrived after this was written or a role that stopped being
 * addressable between the picker loading and the send. Naming both beats naming neither.
 *
 * A 403 is the other answer worth its own words: it means the announce roles no longer include this
 * user, which reads as a bug unless it is spelled out.
 */
const reasonFor = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.code === 403) {
      return 'You are not allowed to send announcements. The roles that may are set in '
        + 'Administration > General Settings.';
    }
    if (error.code === 400) {
      return 'The service turned this announcement away. Check that the recipient is still one it '
        + 'accepts and that any expiry is in the future.';
    }
  }
  return error instanceof Error && error.message ? error.message : 'The announcement could not be sent.';
};

export interface SendNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Publishes an announcement — the one notification type a person writes rather than the service
 * raising it.
 *
 * Deliberately not on a navigable route. It is reached by clicking the logo in the About dialog
 * four times (see VersionInfo), which is enough for the handful of people who need it and keeps a
 * broadcast-to-everybody button off the navigation. The trigger is also gated on the roles allowed
 * to announce, so this never opens for somebody the service would answer 403.
 *
 * <b>That gate is a courtesy, not the control.</b> The service checks the caller against its
 * NOTIFICATION_ANNOUNCE_ROLES parameter on every call and is the only thing standing between a
 * curious user and an announcement — which is why a 403 is handled here as an ordinary outcome
 * rather than treated as impossible.
 */
export const SendNotificationModal: React.FunctionComponent<SendNotificationModalProps> = ({
  isOpen,
  onClose
}) => {
  const [scope, setScope] = React.useState<AUDIENCE_SCOPE>(AUDIENCE_SCOPE.ALL);
  const [audience, setAudience] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [expiresAt, setExpiresAt] = React.useState<Date | null>(null);

  const [isScopeOpen, setIsScopeOpen] = React.useState(false);
  const [isRoleOpen, setIsRoleOpen] = React.useState(false);
  const [roles, setRoles] = React.useState<string[]>([]);

  const [isSending, setIsSending] = React.useState(false);
  const [result, setResult] = React.useState<{ variant: 'success' | 'danger'; text: string } | null>(null);
  // Set once the sender has tried to submit, so the form does not open covered in red.
  const [wasSubmitted, setWasSubmitted] = React.useState(false);

  /**
   * The roles that may be addressed, from the service. The same parameter it validates the
   * announcement against, so the picker cannot offer something the write would reject.
   */
  React.useEffect(() => {
    if (!isOpen) return;
    NotificationApi.audiences()
      .then(setRoles)
      .catch((error) => {
        // Not fatal: only the role picker loses its options, and the other two scopes still work.
        console.warn('[Notifications] Could not load the addressable roles:', error);
        setRoles([]);
      });
  }, [isOpen]);

  const reset = () => {
    setScope(AUDIENCE_SCOPE.ALL);
    setAudience('');
    setTitle('');
    setMessage('');
    setExpiresAt(null);
    setResult(null);
    setWasSubmitted(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const needsAudience = scope !== AUDIENCE_SCOPE.ALL;
  const titleMissing = title.trim().length === 0;
  // Required by the service, which rejects a blank one with a 400 and an empty body — so this has
  // to be caught here or the sender gets "An unknown error occurred" and no idea which field.
  const messageMissing = message.trim().length === 0;
  const audienceMissing = needsAudience && audience.trim().length === 0;
  // The service refuses an expiry in the past. Compared at render rather than at submit so the
  // form says so while the date is being picked.
  const expiryInPast = expiresAt !== null && expiresAt.getTime() <= Date.now();
  const canSend = !titleMissing && !messageMissing && !audienceMissing && !expiryInPast && !isSending;

  const send = async () => {
    setWasSubmitted(true);
    if (!canSend) return;

    setIsSending(true);
    setResult(null);

    const payload: AnnouncementPayload = {
      scope,
      // The service rejects a value on ALL rather than ignoring it — an audience of "everybody, in
      // particular Ricardo" is a contradiction, and it says so instead of guessing which half to keep.
      audience: needsAudience ? audience.trim() : undefined,
      title: title.trim(),
      message: message.trim(),
      expiresAt: expiresAt ? expiresAt.toISOString() : undefined
    };

    try {
      await NotificationApi.announce(payload);
      const to = scope === AUDIENCE_SCOPE.ALL ? 'everyone' : `${labelOf(scope).toLowerCase()}: ${payload.audience}`;
      setResult({ variant: 'success', text: `Sent to ${to}. It is in their bell now.` });

      // Cleared rather than closed, so several announcements can be sent in one sitting and the
      // confirmation stays on screen long enough to be read.
      setTitle('');
      setMessage('');
      setExpiresAt(null);
      setWasSubmitted(false);
    } catch (error: unknown) {
      setResult({ variant: 'danger', text: reasonFor(error) });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal variant={ModalVariant.medium} isOpen={isOpen} onClose={close} aria-label="Send a notification">
      <ModalHeader
        title="Send a notification"
        description="Writes an announcement straight into the recipients' notification panel."
      />
      <ModalBody>
        {result && (
          <Alert
            variant={result.variant}
            isInline
            title={result.text}
            style={{ marginBottom: 'var(--pf-t--global--spacer--md, 16px)' }}
          />
        )}

        <Form onSubmit={(event) => { event.preventDefault(); void send(); }}>
          <FormGroup label="Send to" isRequired fieldId="notification-scope">
            <Select
              isOpen={isScopeOpen}
              selected={scope}
              onOpenChange={setIsScopeOpen}
              onSelect={(_event, value) => {
                setScope(value as AUDIENCE_SCOPE);
                setAudience('');
                setIsScopeOpen(false);
              }}
              // The id belongs on the toggle, not on Select: Select puts its props on the menu,
              // which exists only while open, so the FormGroup label would point at nothing.
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  id="notification-scope"
                  ref={toggleRef}
                  isExpanded={isScopeOpen}
                  onClick={() => setIsScopeOpen(!isScopeOpen)}
                  style={{ width: '100%' }}
                >
                  {labelOf(scope)}
                </MenuToggle>
              )}
            >
              <SelectList>
                {SCOPES.map(({ scope: value, label, description }) => (
                  <SelectOption key={value} value={value} description={description}>
                    {label}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </FormGroup>

          {scope === AUDIENCE_SCOPE.ROLE && (
            <FormGroup label="Role" isRequired fieldId="notification-role">
              <Select
                isOpen={isRoleOpen}
                selected={audience}
                onOpenChange={setIsRoleOpen}
                onSelect={(_event, value) => { setAudience(value as string); setIsRoleOpen(false); }}
                toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                  <MenuToggle
                    id="notification-role"
                    ref={toggleRef}
                    isExpanded={isRoleOpen}
                    onClick={() => setIsRoleOpen(!isRoleOpen)}
                    style={{ width: '100%' }}
                  >
                    {audience || 'Choose a role'}
                  </MenuToggle>
                )}
              >
                <SelectList>
                  {roles.length === 0 ? (
                    <SelectOption isDisabled key="none" value="">No roles can be addressed</SelectOption>
                  ) : (
                    roles.map((role) => <SelectOption key={role} value={role}>{role}</SelectOption>)
                  )}
                </SelectList>
              </Select>
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    Set in Administration &gt; General Settings, under the notification audience roles.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}

          {scope === AUDIENCE_SCOPE.USER && (
            <FormGroup label="Username" isRequired fieldId="notification-user">
              <TextInput
                id="notification-user"
                value={audience}
                onChange={(_event, value) => setAudience(value)}
                validated={wasSubmitted && audienceMissing ? 'error' : 'default'}
                placeholder="Their sign-in name"
              />
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant={wasSubmitted && audienceMissing ? 'error' : 'default'}>
                    {/* The service has no user list to check this against - the users live in Keycloak -
                        so a misspelt name is accepted and reaches nobody. */}
                    Their Keycloak sign-in name. A name that does not exist is accepted and delivered
                    to nobody, so check the spelling.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}

          <FormGroup label="Title" isRequired fieldId="notification-title">
            <TextInput
              id="notification-title"
              value={title}
              onChange={(_event, value) => setTitle(value)}
              validated={wasSubmitted && titleMissing ? 'error' : 'default'}
              placeholder="What this is about"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={wasSubmitted && titleMissing ? 'error' : 'default'}>
                  The line shown in the notification panel. Keep it short.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Message" isRequired fieldId="notification-message">
            <TextArea
              id="notification-message"
              value={message}
              onChange={(_event, value) => setMessage(value)}
              rows={4}
              resizeOrientation="vertical"
              validated={wasSubmitted && messageMissing ? 'error' : 'default'}
              placeholder="The detail, shown under the title"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={wasSubmitted && messageMissing ? 'error' : 'default'}>
                  Required. A notification with only a title tells the reader what happened but not
                  what to do about it, so the service turns one away.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Stop showing it after" fieldId="notification-expires">
            {/* The picker's input is read-only and it has no empty state to pick, so without this
                an expiry set by mistake could not be taken off again. */}
            <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
              <FlexItem>
                <DateTimePicker
                  id="notification-expires"
                  value={expiresAt}
                  onChange={(_event, value) => setExpiresAt(value)}
                />
              </FlexItem>
              {expiresAt && (
                <FlexItem>
                  <Button variant="link" isInline onClick={() => setExpiresAt(null)}>Clear</Button>
                </FlexItem>
              )}
            </Flex>
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={expiryInPast ? 'error' : 'default'}>
                  {expiryInPast
                    ? 'This is in the past. An announcement that has already expired would reach nobody, so pick a later moment or clear the field.'
                    : 'Optional, and worth setting on anything time-bound — it is what retires "maintenance tonight" tomorrow instead of leaving it in every inbox until the retention window drops it.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={() => void send()} isLoading={isSending} isDisabled={!canSend}>
          {isSending ? 'Sending' : 'Send'}
        </Button>
        <Button variant="link" onClick={close}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};
